import FeatureGates from "@atlaskit/feature-gate-js-client";
import { STATSIG_FEATURE_GATES } from "../bootstrap/statsigFeatureGates";
import { getExperiment } from "./experimentClient";

export enum EXPERIMENTS {
  DOM_CONTEXT_IMPROVEMENT = "rovo_extension_dom_improvement",
  NTP_CHAT_GHOST_TEXT = "rovo_extension_ntp_chat_ghost_text",
  NTP_SEARCH_FILTERS = "ntp_search_filters",
  NTP_SEARCH_FILTERS_3P_CONNECTORS = "ntp_search_filters_3p_connectors",
  NTP_AGENTS_DROPDOWN = "rovo_extension_ntp_agents_dropdown",
  DEFAULT_TO_CUSTOM_LINKS = "rovo_extension_default_to_custom_links",

  POST_OFFICE_PROVIDER = "rovo-ext_post_office_provider_no_harm_exp",
  USER_DEFAULT_SETTINGS_REVAMP = "rovo-ext_user_default_settings_revamp",
  NTP_NOTIFICATIONS_BELL = "rovo-extension-ntp-notifications-bell",
  ONBOARDING_BENEFIT_PHASE_1 = "rovo-extension-onboarding-benefit-phase-1",
  GOOGLE_SEARCH_TOGGLE = "rovo_extension_google_search_toggle",

  ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY = "rovo-ext_pcm_highlight_entry",
  ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR = "rovo-ext_pcm_search_behavior",
  SETTINGS_DATA_MIGRATION = "rovo-ext_settings_data_migration",
}

export enum PARAMETERS {
  cohort = "cohort",
  isEnabled = "enabled",
  isDomContextImprovementEnabled = "rovo_extension_dom_context_improvement_enabled",
  isNtpSearchFiltersEnabled = "is_search_filters_enabled",
  is3pConnectorEnabled = "is3pConnectorEnabled",
  ntpAgentsDropdownTreatment = "treatment",
  isSettingsUiRevampEnabled = "is_settings_ui_revamp_enabled",
  isDefaultToCustomLinksEnabled = "isEnabled",

  isPostOfficeProviderEnabled = "isEnabled",
  isUserDefaultSettingsRevamp = "isUserDefaultSettingsRevamp",
  isNotificationsEnabled = "notificationsEnabled",
  newlyWelcomed = "newlyWelcomed",
  isGoogleSearchToggleEnabled = "isEnabled",

  variant = "variant",
  siteSettingsReadSource = "siteSettingsReadSource",
}

export const activeExperiments = [
  {
    experimentName: EXPERIMENTS.DOM_CONTEXT_IMPROVEMENT,
    parameterName: PARAMETERS.isDomContextImprovementEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_CHAT_GHOST_TEXT,
    parameterName: PARAMETERS.cohort,
  },
  {
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
    parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
    parameterName: PARAMETERS.is3pConnectorEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
    parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
  },
  {
    experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
    parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
  },

  {
    experimentName: EXPERIMENTS.POST_OFFICE_PROVIDER,
    parameterName: PARAMETERS.isPostOfficeProviderEnabled,
  },
  {
    experimentName: EXPERIMENTS.USER_DEFAULT_SETTINGS_REVAMP,
    parameterName: PARAMETERS.isUserDefaultSettingsRevamp,
  },
  {
    experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
    parameterName: PARAMETERS.isNotificationsEnabled,
  },
  {
    experimentName: EXPERIMENTS.ONBOARDING_BENEFIT_PHASE_1,
    parameterName: PARAMETERS.newlyWelcomed,
  },
  {
    experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
    parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
  },
  {
    experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
    parameterName: PARAMETERS.variant,
  },
  {
    experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
    parameterName: PARAMETERS.variant,
  },
  {
    experimentName: EXPERIMENTS.SETTINGS_DATA_MIGRATION,
    parameterName: PARAMETERS.siteSettingsReadSource,
  },
];

export const isRovoExtensionDomContextImprovementEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.DOM_CONTEXT_IMPROVEMENT,
    parameterName: PARAMETERS.isDomContextImprovementEnabled,
    defaultValue: false,
  });
};

/** Expose user to DOM context improvement experiment if they send a message in the sidebar */
export const exposeUserToDomContextImprovementExperimentIfValid = (isSidebar: boolean): boolean => {
  if (isSidebar) {
    return isRovoExtensionDomContextImprovementEnabled();
  }

  return false;
};

export type NtpChatGhostCohort = "control" | "variantA" | "variantB";
export const getNtpChatGhostTextCohort = (): NtpChatGhostCohort => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_CHAT_GHOST_TEXT,
    parameterName: PARAMETERS.cohort,
    defaultValue: "control",
  }) as unknown as NtpChatGhostCohort;
};

export const isNtpSearchFiltersEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
    parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
    defaultValue: false,
  });
};

export const isNtpSearchFilters3pConnectorsEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
    parameterName: PARAMETERS.is3pConnectorEnabled,
    defaultValue: false,
  });
};

export type NtpAgentsDropdownTreatment = "control" | "A";
/**
 * - "control": Feature disabled
 * - "A": Show dropdown on Chat tab
 */
export const getNtpAgentsDropdownTreatment = (): NtpAgentsDropdownTreatment => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
    parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
    defaultValue: "control",
  }) as NtpAgentsDropdownTreatment;
};

// https://switcheroo.atlassian.com/ui/gates/6d883a6f-d1ae-451d-a056-51498e1d36b4/key/rovo_extension_default_to_custom_links
// Uses getExperiment (standard exposure) — fires exposure when Shortcuts component renders.
export const isDefaultToCustomLinksEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
    parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
    defaultValue: false,
  });
};

/**
 * Session replay feature gate (Statsig). New recording starts when this gate is enabled — see `SessionReplayInitializer`.
 * While the same Gas browser session id is active, replay is not torn down when the gate becomes false;
 * when Gas mints a new session id, `onAnalyticsWebClientSessionRotation` stops replay if no longer eligible.
 *
 * The experiment `rovo-extension-session-replay` has been replaced by the feature gate
 * `rovo-extension-session-replay-gate`. Use this function which now checks the feature gate instead.
 */
export const isSessionReplayEnabled = (): boolean => {
  return FeatureGates.checkGate(STATSIG_FEATURE_GATES.SESSION_REPLAY);
};

// https://switcheroo.atlassian.com/ui/gates/4a2ff9f8-f377-40e0-9258-4886ba913881/key/rovo-ext_post_office_provider_no_harm_exp
export const isPostOfficeProviderEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.POST_OFFICE_PROVIDER,
    parameterName: PARAMETERS.isPostOfficeProviderEnabled,
    defaultValue: false,
  });
};

// https://switcheroo.atlassian.com/ui/gates/90807101-85da-4321-9be9-219eab788970/key/rovo-ext_user_default_settings_revamp
export const isUserDefaultSettingsRevampEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.USER_DEFAULT_SETTINGS_REVAMP,
    parameterName: PARAMETERS.isUserDefaultSettingsRevamp,
    defaultValue: false,
  });
};

export const isNtpNotificationsBellEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
    parameterName: PARAMETERS.isNotificationsEnabled,
    defaultValue: false,
  });
};

/**
 * Whether the user should see the "newly welcomed" variant of the welcome page as part
 * of the onboarding benefit phase 1 experiment.
 */
export const isNewlyWelcomedEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.ONBOARDING_BENEFIT_PHASE_1,
    parameterName: PARAMETERS.newlyWelcomed,
    defaultValue: false,
  });
};

/**
 * Whether the user should see the Google tab toggle alongside the Search and Chat tabs
 * on the new tab page.
 */
export const isGoogleSearchToggleEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
    parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
    defaultValue: false,
  });
};

export type RovoContextMenuHighlightEntryVariant = "control" | "A" | "B";
export type RovoContextMenuSearchBehaviorVariant = "control" | "A" | "B";

const isRovoContextMenuHighlightEntryVariant = (value: unknown): value is RovoContextMenuHighlightEntryVariant =>
  value === "control" || value === "A" || value === "B";

const isRovoContextMenuSearchBehaviorVariant = (value: unknown): value is RovoContextMenuSearchBehaviorVariant =>
  value === "control" || value === "A" || value === "B";

export const getRovoContextMenuHighlightEntryVariant = (): RovoContextMenuHighlightEntryVariant => {
  const value = getExperiment<RovoContextMenuHighlightEntryVariant>({
    experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
    parameterName: PARAMETERS.variant,
    defaultValue: "control",
  });
  return isRovoContextMenuHighlightEntryVariant(value) ? value : "control";
};

export const getRovoContextMenuSearchBehaviorVariant = (): RovoContextMenuSearchBehaviorVariant => {
  const value = getExperiment<RovoContextMenuSearchBehaviorVariant>({
    experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
    parameterName: PARAMETERS.variant,
    defaultValue: "control",
  });
  return isRovoContextMenuSearchBehaviorVariant(value) ? value : "control";
};

export const getAllRovoContextMenuVariants = (): {
  highlightEntry: RovoContextMenuHighlightEntryVariant;
  searchBehavior: RovoContextMenuSearchBehaviorVariant;
} => ({
  highlightEntry: getRovoContextMenuHighlightEntryVariant(),
  searchBehavior: getRovoContextMenuSearchBehaviorVariant(),
});

export const deriveIsRovoContextMenuHighlightFabEnabled = (variant: RovoContextMenuHighlightEntryVariant): boolean =>
  variant === "A" || variant === "B";

export const deriveIsRovoContextMenuRightClickFabEnabled = (variant: RovoContextMenuHighlightEntryVariant): boolean =>
  variant === "B";

export const deriveIsRovoContextMenuSearchInSidePanelEnabled = (
  variant: RovoContextMenuSearchBehaviorVariant,
): boolean => variant === "A";

export const deriveIsRovoContextMenuSearchFullPageEnabled = (variant: RovoContextMenuSearchBehaviorVariant): boolean =>
  variant === "B";

export const deriveIsRovoContextMenuEnabled = (
  highlightEntry: RovoContextMenuHighlightEntryVariant,
  searchBehavior: RovoContextMenuSearchBehaviorVariant,
): boolean => highlightEntry !== "control" || searchBehavior !== "control";

export const isRovoContextMenuHighlightFabEnabled = (): boolean =>
  deriveIsRovoContextMenuHighlightFabEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuRightClickFabEnabled = (): boolean =>
  deriveIsRovoContextMenuRightClickFabEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuSearchInSidePanelEnabled = (): boolean =>
  deriveIsRovoContextMenuSearchInSidePanelEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuSearchFullPageEnabled = (): boolean =>
  deriveIsRovoContextMenuSearchFullPageEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuEnabled = (): boolean => {
  const { highlightEntry, searchBehavior } = getAllRovoContextMenuVariants();
  return deriveIsRovoContextMenuEnabled(highlightEntry, searchBehavior);
};

export type SiteSettingsReadSource = "legacy" | "rovoextadminsettings";

export const getSiteSettingsReadSource = (): SiteSettingsReadSource => {
  const readSource = getExperiment({
    experimentName: EXPERIMENTS.SETTINGS_DATA_MIGRATION,
    parameterName: PARAMETERS.siteSettingsReadSource,
    defaultValue: "legacy",
  });

  return readSource === "rovoextadminsettings" ? "rovoextadminsettings" : "legacy";
};
